import { useSelector } from 'react-redux';
import { themeConfig } from '../../config/theme-config';
import { layerConfig } from '../../config/layer-config';
import { isUserSignedIn } from '../../reducers/currentUser';

const useStyles = ({ rounded }) => ({
  position: 'absolute',
  left: '30px',
  bottom: '30px',
  // width: '150px',
  // height: '235px',
  borderRadius: rounded ? '5px' : 'inherit',
  overflow: rounded ? 'hidden' : 'inherit',
  backgroundColor: 'white',
  boxShadow: '2px 2px 2px 2px rgba(0, 0, 0, 0.2)',
  padding: '10px',
});

const lineStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '2px',
};

const svgStyle = {
  width: '14px',
  height: '14px',
};

const circleGroupStyle = {
  marginBottom: '10px',
};

const fedFundingStyle = {
  marginTop: '10px',
};

const swatchStyle = {
  display: 'inline-block',
  width: '14px',
  height: '14px',
};

const font = {
  color: 'gray',
  fontSize: '12px',
  lineHeight: 'normal',
};

const labelStyle = {
  ...font,
  marginLeft: '8px',
};

const titleStyle = {
  ...font,
  fontWeight: 'bold',
  marginBottom: '4px',
  width: '130px',
};

const locationServedConfig = layerConfig.find((c) => c.title === 'Locations - Served');
const locationUnderservedConfig = layerConfig.find((c) => c.title === 'Locations - Underserved');
const fedFundingConfig = layerConfig.find((c) => c.title === 'Federal Funding Areas');

const toRgba = (c) => `rgba(${c.join()})`;

const makeCircle = (e, size, strokeColor) => {
  const fill = toRgba(e.fillColor);
  const stroke = toRgba(strokeColor);

  return (
    <div key={e.label} style={lineStyle}>
      <svg viewBox="0 0 14 14" style={svgStyle}>
        <circle cx="7" cy="7" r={size} stroke={stroke} fill={fill} />
      </svg>
      <span style={labelStyle}>{e.label}</span>
    </div>
  );
};

const getLocationContent = (isUserLoggedIn) => {
  const zoom = useSelector(({ mainControls: { zoom } }) => zoom);

  if (zoom < locationUnderservedConfig.minZoom) {
    return;
  }

  let servedCircles;

  if (zoom >= locationServedConfig.minZoom && isUserLoggedIn) {
    const { size, strokeColor } = locationServedConfig;
    const entries = Object.values(locationServedConfig.unique);
    servedCircles = entries.filter((e) => e.label !== 'default').map((e) => makeCircle(e, size, strokeColor));
  }

  const { size, strokeColor } = locationUnderservedConfig;
  const entries = Object.values(locationUnderservedConfig.unique);
  const underservedCircles = entries.filter((e) => e.label !== 'default').map((e) => makeCircle(e, size, strokeColor));

  return (
    <div style={circleGroupStyle}>
      {servedCircles}
      {underservedCircles}
    </div>
  );
};

const makeSwatch = (e, strokeColor, strokeWidth) => {
  const backgroundColor = toRgba(e.fillColor);
  const border = strokeWidth > 0 ? `${strokeWidth}px solid ${toRgba(strokeColor)}` : undefined;

  return (
    <div key={e.label} style={lineStyle}>
      <div style={{ backgroundColor, border, ...swatchStyle }}> </div>
      <span style={labelStyle}>{e.label}</span>
    </div>
  );
};

const getThemeContent = (theme) => {
  const entries = theme.ramp || Object.values(theme.unique);
  const swatches = entries.map((e) => makeSwatch(e, theme.strokeColor, 1));

  return (
    <>
      <div style={titleStyle}>{theme.title}</div>
      {swatches}
    </>
  );
};

const getFedFundingContent = ({ theme, isUserLoggedIn }) => {
  if (theme !== 'Federal Funding' || !isUserLoggedIn) {
    return;
  }

  const entries = Object.values(fedFundingConfig.unique);
  const swatches = entries.map((e) => makeSwatch(e));

  return (
    <div style={fedFundingStyle}>
      <div style={titleStyle}>Federal Funding Areas</div>
      {swatches}
    </div>
  );
};

const Legend = ({ rounded }) => {
  const styles = useStyles({ rounded });
  const isUserLoggedIn = useSelector(isUserSignedIn);
  const theme = useSelector(({ mainControls: { theme } }) => theme);
  const themeContent = getThemeContent(themeConfig[theme]);
  const locationContent = getLocationContent(isUserLoggedIn);
  const fedFundingContent = getFedFundingContent({ theme, isUserLoggedIn });

  return (
    <div id="legend" style={styles}>
      {locationContent}
      {themeContent}
      {fedFundingContent}
    </div>
  );
};

export default Legend;
